export const DEFAULT_RSO_LEVEL = 5;
export const RSO_MIN_LEVEL = 0;
export const RSO_MAX_LEVEL = 10;

export type RsoConfigurationSource = 'default' | 'saved' | 'manual';

export interface IRsoConstraintLevels {
    minimiseTravelTime: number;
    skillCompliance: number;
    minimiseCost: number;
    contractAndPreferredHours: number;
    continuityOfCare: number;
    fairDistribution: number;
}

export interface IRsoConfigurationPreset {
    id: string;
    name: string;
    description?: string | null;
    levels: IRsoConstraintLevels;
    createdOn?: string | null;
    modifiedOn?: string | null;
}

export const RSO_CONSTRAINT_LEVEL_FIELDS = [
    'minimiseTravelTime',
    'skillCompliance',
    'minimiseCost',
    'contractAndPreferredHours',
    'continuityOfCare',
    'fairDistribution'
] as const;

export type RsoConstraintLevelField = typeof RSO_CONSTRAINT_LEVEL_FIELDS[number];

export const LEGACY_RSO_CONSTRAINT_LEVEL_FIELDS = [
    'leaveAndNonAvailability',
    'shiftConflictAvoidance',
    'geographicFeasibility',
    'skillExpiry',
    'breakBetweenShifts',
    'maxDailyHours',
    'maxWeeklyHours',
    'reduceOvertime',
    'meetContractedHours',
    'respectPreferredHours',
    'stayWithinAvailableHours'
] as const;

export const RSO_CONSTRAINT_LEVEL_LABELS: Record<keyof IRsoConstraintLevels, string> = {
    minimiseTravelTime: 'Minimise travel time',
    skillCompliance: 'Skill compliance',
    minimiseCost: 'Minimise cost',
    contractAndPreferredHours: 'Contract and preferred hours',
    continuityOfCare: 'Continuity of care',
    fairDistribution: 'Fair distribution'
};

const clampLevelValue = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return DEFAULT_RSO_LEVEL;
    }
    return Math.max(RSO_MIN_LEVEL, Math.min(RSO_MAX_LEVEL, Math.round(parsed)));
};

export const createDefaultRsoConstraintLevels = (fillValue: number = DEFAULT_RSO_LEVEL): IRsoConstraintLevels => ({
    minimiseTravelTime: fillValue,
    skillCompliance: fillValue,
    minimiseCost: fillValue,
    contractAndPreferredHours: fillValue,
    continuityOfCare: fillValue,
    fairDistribution: fillValue
});

const unwrapRsoConstraintLevels = (levels?: Partial<IRsoConstraintLevels> | null): Record<string, unknown> | null => {
    if (!levels || typeof levels !== 'object') {
        return null;
    }

    const candidate = levels as Record<string, unknown>;
    if ('rsoConstraintLevels' in candidate && candidate.rsoConstraintLevels && typeof candidate.rsoConstraintLevels === 'object') {
        return candidate.rsoConstraintLevels as Record<string, unknown>;
    }

    return candidate;
};

const hasLegacyRsoConstraintLevels = (levels: Record<string, unknown>): boolean =>
    LEGACY_RSO_CONSTRAINT_LEVEL_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(levels, field));

export const normalizeRsoConstraintLevels = (
    levels?: Partial<IRsoConstraintLevels> | null
): IRsoConstraintLevels => {
    const defaults = createDefaultRsoConstraintLevels();
    const candidate = unwrapRsoConstraintLevels(levels);
    if (!candidate) {
        return defaults;
    }

    if (hasLegacyRsoConstraintLevels(candidate)) {
        return defaults;
    }

    const normalized = { ...defaults };
    RSO_CONSTRAINT_LEVEL_FIELDS.forEach((field) => {
        normalized[field] = clampLevelValue(candidate[field]);
    });

    return normalized;
};

export const cloneRsoConstraintLevels = (levels: IRsoConstraintLevels): IRsoConstraintLevels => ({
    ...normalizeRsoConstraintLevels(levels)
});

export const getRsoConstraintLevelEntries = (levels: IRsoConstraintLevels) =>
    RSO_CONSTRAINT_LEVEL_FIELDS.map((field) => ({
        key: field,
        label: RSO_CONSTRAINT_LEVEL_LABELS[field],
        value: levels[field]
    }));
